import React, { useMemo, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// AlberoRicorsi — resa gerarchica dei risultati di /finanziario/ricorsi-albero
//   famiglie:   [{ radice, nodi: [{codice, livello, padri, figli, cumulativo, trovato}], riferimenti: [...] }]
//   risultati:  schede piatte (una per gestione_ricorso; un codice può averne più di una)
//   renderCard: (scheda) => <RicorsoCard .../>   (riusa la card dell'elenco senza modifiche)
//   onApriRicorso: (codice) => void
// Regole di resa:
//   - rientro di 28 px per livello con linea di legame a sinistra
//   - nodo con più padri = "cumulativo": blocco viola tratteggiato con l'elenco dei padri
//   - riferimenti = cumulativi assegnati a un'altra famiglia: riga sintetica cliccabile
//   - antenati non trovati (contesto) resi attenuati
//   - figli apri/chiudi: aperti se il sottoalbero contiene un nodo trovato o è piccolo
// ─────────────────────────────────────────────────────────────────────────────

const RIENTRO = 40;
const RAIL = 14;          // distanza della linea di legame dal bordo sinistro del blocco figlio
const VIOLA = '#8E44AD';

const ETICHETTE_GRADO = {
  primo_grado: 'Primo grado', appello: 'Appello',
  ottemperanza: 'Ottemperanza', ottemperanza_ottemperanza: 'Ott. di ottemperanza',
};

export default function AlberoRicorsi({ famiglie, risultati, renderCard, onApriRicorso }) {
  const [chiusi, setChiusi] = useState(() => new Set());

  // codice -> schede (possono essere più di una per codice condiviso)
  const schedePer = useMemo(() => {
    const m = {};
    (risultati || []).forEach(r => { (m[r.codice_ricorso] = m[r.codice_ricorso] || []).push(r); });
    return m;
  }, [risultati]);

  const toggle = (codice) => setChiusi(prev => {
    const n = new Set(prev);
    if (n.has(codice)) n.delete(codice); else n.add(codice);
    return n;
  });

  if (!famiglie || !famiglie.length) {
    return <div style={{ fontSize: 13, color: '#a0aec0', padding: 16 }}>Nessun ricorso trovato.</div>;
  }

  return (
    <div>
      {famiglie.map(f => <Famiglia key={f.radice} famiglia={f} schedePer={schedePer}
        renderCard={renderCard} onApriRicorso={onApriRicorso} chiusi={chiusi} toggle={toggle} />)}
    </div>
  );
}

function Famiglia({ famiglia, schedePer, renderCard, onApriRicorso, chiusi, toggle }) {
  const nodi = useMemo(() => famiglia.nodi || [], [famiglia.nodi]);
  const perCodice = useMemo(() => Object.fromEntries(nodi.map(n => [n.codice, n])), [nodi]);
  const inFamiglia = (c) => !!perCodice[c];

  // sottoalbero contiene un nodo trovato? (per decidere apertura di default)
  const contieneTrovato = (codice, visti = new Set()) => {
    if (visti.has(codice)) return false;
    visti.add(codice);
    const n = perCodice[codice];
    if (!n) return false;
    return (n.figli || []).some(c => inFamiglia(c) && (perCodice[c].trovato || contieneTrovato(c, visti)));
  };
  const apertoDefault = (n) => nodi.length <= 4 || contieneTrovato(n.codice);

  // riferimenti raggruppati per padre (compaiono sotto il nodo padre presente in famiglia)
  const riferimentiPer = useMemo(() => {
    const m = {};
    (famiglia.riferimenti || []).forEach(r => {
      // il riferimento sta sotto il nodo di questa famiglia che è suo padre; se non identificabile, sotto la radice
      const padreQui = nodi.find(n => (n.figli || []).includes(r.codice));
      const k = padreQui ? padreQui.codice : famiglia.radice;
      (m[k] = m[k] || []).push(r);
    });
    return m;
  }, [famiglia, nodi]);

  const renderNodo = (codice, livello, visti, isLast = true) => {
    if (visti.has(codice)) return null;
    visti.add(codice);
    const n = perCodice[codice];
    if (!n) return null;
    const schede = schedePer[codice] || [];
    const figli = (n.figli || []).filter(inFamiglia);
    const refs = riferimentiPer[codice] || [];
    const haDiscendenti = figli.length + refs.length > 0;
    const aperto = chiusi.has(codice) ? false : (chiusi.has('!' + codice) ? true : apertoDefault(n));
    const contesto = !n.trovato;

    const toggleBtn = haDiscendenti && (
      <button onClick={() => {
          if (aperto) { toggle(codice); if (chiusi.has('!' + codice)) toggle('!' + codice); }
          else { if (chiusi.has(codice)) toggle(codice); else toggle('!' + codice); }
        }}
        title={aperto ? 'Nascondi i ricorsi collegati' : 'Mostra i ricorsi collegati'}
        style={{ position: 'absolute', right: 16, bottom: -9, zIndex: 1, padding: '1px 9px', borderRadius: 10, fontSize: 10,
          fontWeight: 600, background: 'white', border: '1px solid #BEE3F8', color: '#2E75B6', cursor: 'pointer', lineHeight: '16px' }}>
        {aperto ? '▾' : '▸'} {figli.length + refs.length} collegat{figli.length + refs.length === 1 ? 'o' : 'i'}
        {!aperto && figli.length > 0 && (
          <span style={{ color: '#718096', fontWeight: 400, marginLeft: 4 }}>
            ({figli.map(c => ETICHETTE_GRADO[(schedePer[c] || [])[0]?.grado] || 'ricorso').join(', ')})
          </span>
        )}
      </button>
    );

    const contenuto = (
      <div style={{ opacity: contesto ? 0.72 : 1, position: 'relative' }}>
        {schede.length === 0 && (
          <div style={{ fontSize: 12, color: '#a0aec0', padding: '6px 0' }}>
            {codice} <span style={{ fontSize: 11 }}>(nessuna scheda)</span>
          </div>
        )}
        {schede.map(s => <div key={s.id}>{renderCard(s)}</div>)}
        {!n.cumulativo && toggleBtn}
      </div>
    );

    return (
      <div key={codice} style={{ position: 'relative', marginLeft: livello ? RIENTRO : 0 }}>
        {livello > 0 && (
          <>
            {/* linea di legame: il binario verticale corre per tutta l'altezza del blocco (fino al tick dell'ultimo figlio) */}
            <div style={{ position: 'absolute', left: -RIENTRO + RAIL, top: -12,
              height: isLast ? 36 : 'calc(100% + 12px)', width: 2, background: '#CBD5E0' }} />
            <div style={{ position: 'absolute', left: -RIENTRO + RAIL, top: 24, width: RIENTRO - RAIL - 2, height: 2, background: '#CBD5E0' }} />
          </>
        )}

        {n.cumulativo ? (
          <div style={{ border: `1.5px dashed ${VIOLA}`, borderRadius: 12, padding: '8px 10px 2px', marginBottom: 12, background: '#FBF7FD', position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8, fontSize: 12 }}>
              <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: VIOLA, color: 'white' }}>
                CUMULATIVA · {n.padri.length} RICORSI
              </span>
              <span style={{ color: '#718096' }}>raggruppa:</span>
              {n.padri.map(p => {
                const qui = inFamiglia(p);   // il padre di questa famiglia: "sei qui"
                return (
                  <button key={p} onClick={() => !qui && onApriRicorso && onApriRicorso(p)}
                    title={qui ? 'Ricorso padre di questa famiglia (è la card qui sopra)' : 'Apri il ricorso'}
                    style={{ background: qui ? VIOLA : 'white', border: `1px solid ${qui ? VIOLA : '#E2D4EC'}`, borderRadius: 6,
                      padding: '2px 8px', fontSize: 11, color: qui ? 'white' : VIOLA, cursor: qui ? 'default' : 'pointer',
                      fontFamily: 'monospace', fontWeight: qui ? 700 : 400 }}>
                    {qui ? '● ' : ''}{p}{qui ? '' : ' ↗'}
                  </button>
                );
              })}
            </div>
            {contenuto}
            {toggleBtn}
          </div>
        ) : contenuto}

        {haDiscendenti && aperto && (
          <div>
            {figli.map((c, i) => renderNodo(c, livello + 1, visti, i === figli.length - 1 && refs.length === 0))}
            {refs.map((r, i) => (
              <div key={'ref-' + r.codice} style={{ position: 'relative', marginLeft: RIENTRO, marginBottom: 10 }}>
                <div style={{ position: 'absolute', left: -RIENTRO + RAIL, top: -12,
                  height: i === refs.length - 1 ? 26 : 'calc(100% + 12px)', width: 2, background: '#CBD5E0' }} />
                <div style={{ position: 'absolute', left: -RIENTRO + RAIL, top: 14, width: RIENTRO - RAIL - 2, height: 2, background: '#CBD5E0' }} />
                <button onClick={() => onApriRicorso && onApriRicorso(r.codice)}
                  title={`Ricorso cumulativo mostrato nella famiglia di ${r.famiglia}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 10,
                    border: `1px dashed ${VIOLA}`, background: 'white', color: VIOLA, cursor: 'pointer', fontSize: 12 }}>
                  <span style={{ fontSize: 10, fontWeight: 700 }}>CUMULATIVA</span>
                  <span style={{ fontFamily: 'monospace' }}>{r.codice}</span>
                  <span style={{ color: '#718096' }}>→ vedi nella famiglia di {r.famiglia}</span>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: 6 }}>
      {renderNodo(famiglia.radice, 0, new Set())}
    </div>
  );
}
